import React, { useState, memo } from 'react';
import { useSelector } from 'react-redux';
import {
    ChevronDownIcon,
    ChevronRightIcon,
    PlusIcon,
} from '../../../automationStudioIcons';
import { InputBox } from '../../../../FormComponent';
import ButtonComponent from '../../../../customButton';
import { EXECUTION_STATUS } from '../../../../AutomationStudio/hooks/useReplayExecution';
import { shouldShowRecordingSkipUi } from '../../../../AutomationStudio/utils/replayEligibility';
import { resolveReplayStepStatus } from '../../../../AutomationStudio/utils/replayProgressDisplay';
import { resolveStepIndexForReplayLookup } from './instructionHierarchyUtils';
import { stepHasValidationErrors } from '../../../../../features/automationStudioNew/validationUtils';
import { selectDirtyStepIds } from '../../../../../features/automationStudioNew/automationStudioNewSelectors';
import StepRow from './StepRow';
import styles from '../../../../../styles/automationStudio/automationStudio.module.scss';

function InstructionItemInner({
    instruction,
    instructionNumber,
    displayText,
    isSelected,
    onInstructionSelect,
    onStepSelect,
    selectedStepId,
    selectedStepIds = [],
    isExpanded,
    onToggleExpand,
    isRunning,
    onAddStep,
    instructionId,
    replayProgressMap = {},
    replayExecutionResults = null,
    flatStepIndexStart = 0,
    flatStepsForReplay = [],
    isReplayExecuting = false,
    replayCurrentStepIndex = null,
    replayHighlightFlatIndex = null,
    generationActiveFlatIndex = null,
    isLoadedFromTestcase = false,
    hasExecutedInSession = false,
    isReplayFlow = false,
    isSavedPlanIdle = false,
    isLiveRunActive = false,
    allInstructions = [],
    generationStatus = null,
    variablesList = [],
    variablesListDescriptions = {},
    fileSuggestions = [],
    stepValidationErrors = {},
    onStepIdSelect,
    onStepContextMenu,
}) {
    const dirtyStepIds = useSelector(selectDirtyStepIds);
    const steps = instruction.aiSteps || [];
    const hasSteps = steps.length > 0;
    const [addStepForm, setAddStepForm] = useState(null);
    const [addStepDescription, setAddStepDescription] = useState('');

    const showAddStep = !isRunning && typeof onAddStep === 'function' && hasSteps;

    const handleHeaderClick = () => {
        onToggleExpand(instructionId);
        onInstructionSelect(instructionId);
    };

    const handleAddStepSubmit = (afterIndex) => {
        const desc = addStepDescription.trim();
        if (desc && onAddStep) {
            onAddStep(instructionId, afterIndex, desc);
            setAddStepForm(null);
            setAddStepDescription('');
        }
    };

    const handleAddStepCancel = () => {
        setAddStepForm(null);
        setAddStepDescription('');
    };

    const openAddStepBelow = (e, afterIndex) => {
        e.stopPropagation();
        setAddStepForm(afterIndex);
        setAddStepDescription('');
    };

    const genBorderClass = generationStatus === 'inprogress'
        ? styles.instructionGroupInprogress
        : generationStatus === 'skipped'
            ? styles.instructionGroupSkipped
            : '';

    return (
        <div
            className={`${styles.instructionGroup} ${isSelected ? styles.selected : ''} ${!isExpanded ? styles.collapsed : ''
                } ${genBorderClass}`}
        >
            <div className={styles.instructionHeader} onClick={handleHeaderClick}>
                <div className={styles.instructionNumber}>{instructionNumber}</div>
                <div className={styles.instructionContent}>
                    <span className={`${styles.tooltipHost} ${styles.instructionText} ${styles.asTypoBody2}`} title={displayText}>
                        {displayText}
                    </span>
                    <span className={`${styles.instructionSummary} ${styles.asTypoCaption}`}>
                        {steps.length} step{steps.length !== 1 ? 's' : ''}
                    </span>
                </div>
                {isExpanded ? (
                    <ChevronDownIcon className={styles.instructionChevron} />
                ) : (
                    <ChevronRightIcon className={styles.instructionChevron} />
                )}
            </div>

            {isExpanded && hasSteps && (
                <div className={styles.instructionSteps} role="listbox">
                    {steps.map((step, index) => {
                        const flatStepIndex = flatStepIndexStart + index;
                        const stepIndexForLookup = resolveStepIndexForReplayLookup(
                            step,
                            flatStepIndex,
                            flatStepsForReplay
                        );
                        const baseReplayStatus = resolveReplayStepStatus(
                            step,
                            stepIndexForLookup,
                            replayProgressMap,
                            isReplayFlow,
                            allInstructions
                        );
                        const isGenerationActiveStep =
                            !isReplayExecuting &&
                            isRunning &&
                            generationActiveFlatIndex != null &&
                            generationActiveFlatIndex === stepIndexForLookup;
                        const isCurrentStep =
                            isReplayExecuting && replayHighlightFlatIndex != null
                                ? replayHighlightFlatIndex === stepIndexForLookup
                                : isGenerationActiveStep
                                    || replayCurrentStepIndex === stepIndexForLookup
                                    || (replayCurrentStepIndex === null && stepIndexForLookup === 0 && isReplayExecuting);
                        const effectiveStatus = isReplayExecuting
                            ? (
                                (baseReplayStatus === EXECUTION_STATUS.PENDING ||
                                    baseReplayStatus === EXECUTION_STATUS.SKIPPED) &&
                                isCurrentStep &&
                                baseReplayStatus !== EXECUTION_STATUS.SKIPPED
                                    ? EXECUTION_STATUS.IN_PROGRESS
                                    : baseReplayStatus
                            )
                            : isGenerationActiveStep
                                ? EXECUTION_STATUS.IN_PROGRESS
                                : baseReplayStatus;
                        const isRecordingSkipStep = shouldShowRecordingSkipUi(step, allInstructions, {
                            isReplayFlow,
                            isSavedPlanIdle,
                            isLiveRunActive,
                            replayStatus: effectiveStatus,
                        });
                        const isReplayActiveRow =
                            replayHighlightFlatIndex != null &&
                            replayHighlightFlatIndex === stepIndexForLookup;
                        const isGenerationActiveRow = isGenerationActiveStep;
                        const isMultiSelected = selectedStepIds.includes(step.id);
                        const hasValidationError = stepHasValidationErrors(stepValidationErrors?.[step.id]);
                        const isStepDirty = dirtyStepIds.includes(String(step.id));

                        return (
                            <div
                                key={step.id || index}
                                className={`${styles.stepRowWrapper} ${addStepForm === index ? styles.showAddStep : ''}`}
                                data-replay-scroll-index={stepIndexForLookup}
                            >
                                <StepRow
                                    step={step}
                                    stepNumber={step.stepIndex + 1}
                                    isSelected={
                                        isReplayActiveRow ||
                                        isGenerationActiveRow ||
                                        (!isReplayExecuting && !isRunning && selectedStepId === step.id)
                                    }
                                    isMultiSelected={isMultiSelected}
                                    onStepActivate={onStepSelect}
                                    onStepSelect={onStepIdSelect}
                                    replayStatus={effectiveStatus}
                                    replayStepDetail={replayExecutionResults?.stepDetails?.[stepIndexForLookup]}
                                    executionConfidence={(() => {
                                        const detail = replayExecutionResults?.stepDetails?.[stepIndexForLookup];
                                        return detail?.result?.result?.confidence ?? detail?.result?.details?.confidence ?? null;
                                    })()}
                                    isLoadedFromTestcase={isLoadedFromTestcase}
                                    hasExecutedInSession={hasExecutedInSession}
                                    isReplayFlow={isReplayFlow}
                                    isRecordingSkipStep={isRecordingSkipStep}
                                    isSavedPlanIdle={isSavedPlanIdle}
                                    isLiveRunActive={isLiveRunActive}
                                    allInstructions={allInstructions}
                                    variablesList={variablesList}
                                    variablesListDescriptions={variablesListDescriptions}
                                    fileSuggestions={fileSuggestions}
                                    hasValidationError={hasValidationError}
                                    isDirty={isStepDirty}
                                    onContextMenu={onStepContextMenu}
                                />
                                {showAddStep && (
                                    <div className={styles.addStepButtonContainer}>
                                        {addStepForm === index ? (
                                            <div className={styles.addStepForm} onClick={(e) => e.stopPropagation()}>
                                                <div className={styles.addStepFormInput}>
                                                    <InputBox
                                                        size="small"
                                                        placeholder="Step description..."
                                                        value={addStepDescription}
                                                        onChange={(e) => setAddStepDescription(e.target.value)}
                                                        onKeyDown={(e) => e.stopPropagation()}
                                                        autoFocus
                                                    />
                                                </div>
                                                <div className={styles.addStepFormFooter}>
                                                    <ButtonComponent
                                                        ctaText="Add"
                                                        variant="primary"
                                                        size="small"
                                                        className="white bg-green600"
                                                        onClick={() => handleAddStepSubmit(index)}
                                                        disabled={!addStepDescription.trim()}
                                                    />
                                                    <ButtonComponent
                                                        ctaText="Cancel"
                                                        variant="secondary"
                                                        size="small"
                                                        className="slate900 bg-slate50"
                                                        onClick={handleAddStepCancel}
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                className={styles.addStepBelowButton}
                                                onClick={(e) => openAddStepBelow(e, index)}
                                                aria-label={`Add Step below step ${index + 1}`}
                                            >
                                                <PlusIcon className={styles.addStepBelowIcon} aria-hidden />
                                                Add Step below
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {isExpanded && !hasSteps && generationStatus === 'inprogress' && (
                <div className={styles.genInstructionLoader}>
                    <div className={styles.genPulseBar} />
                    <div className={styles.genPulseBar} style={{ width: '60%', animationDelay: '0.2s' }} />
                </div>
            )}
        </div>
    );
}

function instructionItemPropsAreEqual(prev, next) {
    if (prev.instruction !== next.instruction) return false;
    if (prev.instructionNumber !== next.instructionNumber) return false;
    if (prev.displayText !== next.displayText) return false;
    if (prev.isSelected !== next.isSelected) return false;
    if (prev.selectedStepId !== next.selectedStepId) return false;
    if (prev.selectedStepIds !== next.selectedStepIds) return false;
    if (prev.isExpanded !== next.isExpanded) return false;
    if (prev.isRunning !== next.isRunning) return false;
    if (prev.instructionId !== next.instructionId) return false;
    if (prev.replayProgressMap !== next.replayProgressMap) return false;
    if (prev.replayExecutionResults !== next.replayExecutionResults) return false;
    if (prev.flatStepIndexStart !== next.flatStepIndexStart) return false;
    if (prev.flatStepsForReplay !== next.flatStepsForReplay) return false;
    if (prev.isReplayExecuting !== next.isReplayExecuting) return false;
    if (prev.replayCurrentStepIndex !== next.replayCurrentStepIndex) return false;
    if (prev.replayHighlightFlatIndex !== next.replayHighlightFlatIndex) return false;
    if (prev.generationActiveFlatIndex !== next.generationActiveFlatIndex) return false;
    if (prev.isLoadedFromTestcase !== next.isLoadedFromTestcase) return false;
    if (prev.hasExecutedInSession !== next.hasExecutedInSession) return false;
    if (prev.isReplayFlow !== next.isReplayFlow) return false;
    if (prev.isSavedPlanIdle !== next.isSavedPlanIdle) return false;
    if (prev.isLiveRunActive !== next.isLiveRunActive) return false;
    if (prev.allInstructions !== next.allInstructions) return false;
    if (prev.generationStatus !== next.generationStatus) return false;
    if (prev.variablesList !== next.variablesList) return false;
    if (prev.variablesListDescriptions !== next.variablesListDescriptions) return false;
    if (prev.fileSuggestions !== next.fileSuggestions) return false;
    if (prev.stepValidationErrors !== next.stepValidationErrors) return false;
    if (prev.onInstructionSelect !== next.onInstructionSelect) return false;
    if (prev.onStepSelect !== next.onStepSelect) return false;
    if (prev.onToggleExpand !== next.onToggleExpand) return false;
    if (prev.onAddStep !== next.onAddStep) return false;
    if (prev.onStepIdSelect !== next.onStepIdSelect) return false;
    if (prev.onStepContextMenu !== next.onStepContextMenu) return false;
    return true;
}

const InstructionItem = memo(InstructionItemInner, instructionItemPropsAreEqual);

export default InstructionItem;
